import React, { FC, useEffect } from 'react'
import { Card, Space, Tooltip, Typography, Image } from 'antd'
import { formatCurrency, getPlanMultiplier, getPricePeriod, getResourceType, SelectedProduct } from '../utils'
import { usePaymentMethod, useShopping } from '../hooks'
import { StyledInputNumber } from './StyledInputNumber'
import { InfoCircleFilled } from '@ant-design/icons'

const { Text, Title } = Typography

interface SeatCardProps {
  productDetails: SelectedProduct
}

export const SeatCard: FC<SeatCardProps> = ({
  productDetails
}) => {
  const { product, price, meta } = productDetails

  const { chart, setChart } = useShopping()
  const { billingPeriod } = usePaymentMethod()

  const unitPrice = parseFloat(price.unit_price.amount)
  const resourceCount = getPlanMultiplier(price)
  const perUnitPrice = resourceCount > 1 ? unitPrice / resourceCount : unitPrice

  useEffect(() => {
    setChart(prev => {
      return({
      ...prev,
      [price.name as string]: {
        key: price.name as string,
        quantity: prev[price.name as string]?.quantity || 0,
        product: productDetails,
        isValid: !productDetails.meta?.hasConflict
      }
      })
    })
  }, [productDetails, setChart, billingPeriod])

  return (
    <Card size={'small'}
      style={{
        backgroundColor: meta?.hasConflict 
        ? '#f5f5f5' 
        : price.name as string in chart 
          && chart[price.name as string]?.quantity 
          && chart[price.name as string]?.quantity > 0 
          ? '#E6F7FF' 
          : undefined,
        transition: 'background-color 0.5s ease'
      }}
      title={
        <Space size={'small'}>
          <Image src={product.image_url} 
            alt={product.name} 
            preview={false}
            style={{ width: '26px' }} 
          />
          <Text strong>
            {price.name}
          </Text>
          <Tooltip title={product.description}>
            <InfoCircleFilled style={{ 
              color: '#1890ff',
              marginLeft: 0
            }} />
          </Tooltip>
      </Space>
      }
    >
      <Space 
        direction="vertical" 
        style={{ width: '100%', height: 80 }} 
        size="small"
      >
        <div>
          <Space size={'small'}>
            <Title level={5} style={{ margin: 0, color: '#1890ff' }}>
              {formatCurrency(unitPrice, price.unit_price.currency_code)}
            </Title>
            <Text type="secondary" style={{ fontSize: '12px', fontWeight: 'normal' }}>
              / {getPricePeriod(price) === 'yearly' ? 'year' : 'month'}
            </Text>
          </Space>
          {resourceCount > 1 && (
            <Text type="secondary" style={{ fontSize: '12px', fontWeight: 'normal', display: 'block' }}>
              ({formatCurrency(perUnitPrice, price.unit_price.currency_code)} per {getResourceType(product).singular})
            </Text>
          )}
        </div>
      </Space>
      <div style={{ 
        position: 'relative',  
        textAlign: 'right',
        marginTop: 'auto',
        width: '100%'
      }}>
        <StyledInputNumber
          min={0}
          value={
            price.name as string in chart 
            ? chart[price.name as string]?.quantity : 0}
            onChange={(val) => {
              setChart(prev => ({
              ...prev,
              [price.name as string]: {
                ...prev[price.name as string],
                quantity: Number(val)
              }
            }))
          }}
          addonBefore={<Typography.Text strong>Qty</Typography.Text>}
          style={{ maxWidth: 120 }}
        />
      </div>
    </Card>
  )
}
